/**
 * Multi-language URL routing helpers.
 *
 * SEO design:
 *   - Default (English)  → /about, /products, /news, ...
 *   - Chinese (zh-TW)    → /zh/about, /zh/products, ...
 *   - Japanese           → /ja/about, /ja/products, ...
 *
 * The build step pre-renders a static HTML file for every (language × route)
 * pair so Google can index each language version separately. hreflang tags
 * link the variants together.
 */

export type SupportedLang = 'zh' | 'en' | 'ja';

export const SUPPORTED_LANGS: SupportedLang[] = ['zh', 'en', 'ja'];

export const DEFAULT_LANG: SupportedLang = 'en';

/** URL prefix per language (default English sits at site root). */
export const LANG_PREFIX: Record<SupportedLang, string> = {
  en: '',
  zh: '/zh',
  ja: '/ja',
};

/** Legacy `/en/...` paths from the previous URL scheme (English was prefixed). */
export function stripLegacyEnPrefix(pathname: string): string {
  if (pathname === '/en') return '/';
  if (pathname.startsWith('/en/')) return pathname.slice(3) || '/';
  return pathname;
}

/** `<html lang="...">` value per language, also used by hreflang. */
export const HTML_LANG: Record<SupportedLang, string> = {
  zh: 'zh-TW',
  en: 'en',
  ja: 'ja',
};

/** OpenGraph locale per language. */
export const OG_LOCALE: Record<SupportedLang, string> = {
  zh: 'zh_TW',
  en: 'en_US',
  ja: 'ja_JP',
};

/** Detect the language from a pathname like `/zh/about` → `zh`. */
export function detectLangFromPath(pathname: string): SupportedLang {
  if (!pathname) return DEFAULT_LANG;
  const normalized = stripLegacyEnPrefix(pathname);
  const seg = normalized.split('/').filter(Boolean)[0];
  if (seg === 'zh') return 'zh';
  if (seg === 'ja') return 'ja';
  return DEFAULT_LANG;
}

/** Strip the language prefix from a pathname. `/zh/about` → `/about`. */
export function stripLangPrefix(pathname: string): string {
  const normalized = stripLegacyEnPrefix(pathname);
  const lang = detectLangFromPath(normalized);
  if (lang === DEFAULT_LANG) return normalized || '/';
  const prefix = LANG_PREFIX[lang];
  if (normalized === prefix) return '/';
  if (normalized.startsWith(prefix + '/')) return normalized.slice(prefix.length);
  return normalized || '/';
}

/** Get the basename for BrowserRouter based on the current URL. */
export function getRouterBasename(): string {
  if (typeof window === 'undefined') return '/';
  const lang = detectLangFromPath(window.location.pathname);
  const prefix = LANG_PREFIX[lang];
  return prefix || '/';
}

/**
 * Build a full URL path for a (language, route-without-prefix) pair.
 * e.g. localizePath('/about', 'zh') → '/zh/about'
 *      localizePath('/', 'ja')      → '/ja'
 *      localizePath('/about', 'en') → '/about'
 */
export function localizePath(path: string, lang: SupportedLang): string {
  const clean = path.startsWith('/') ? path : '/' + path;
  const prefix = LANG_PREFIX[lang];
  if (!prefix) return clean;
  if (clean === '/') return prefix;
  return prefix + clean;
}

/**
 * Given the current full pathname (which may already contain a language
 * prefix), produce the equivalent URL for the target language.
 */
export function switchLangUrl(currentPathname: string, targetLang: SupportedLang): string {
  const noPrefix = stripLangPrefix(currentPathname);
  return localizePath(noPrefix, targetLang);
}

/** Normalize an i18n.language value (e.g. "zh-TW", "en-US") to SupportedLang. */
export function normalizeI18nLang(lang: string | undefined): SupportedLang {
  if (!lang) return DEFAULT_LANG;
  const lower = lang.toLowerCase();
  if (lower.startsWith('zh')) return 'zh';
  if (lower.startsWith('ja')) return 'ja';
  if (lower.startsWith('en')) return 'en';
  return DEFAULT_LANG;
}
